import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import { ArrowRight } from '@mui/icons-material';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import moment from 'moment';
import format from 'string-template';

import { ReactComponent as ViewIcon } from '../../../../../../assets/icons/v2/arrow-right.svg';
import { Section } from '../../../../../../Model/CoachNotificationView';
import LoggedInUserContext from '../../../../../../context/LoggedInUserContext';
import useQuickSearch from '../../../../../../hooks/useQuickSearch';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';
import { PillButton, PrimaryButton } from '../../../../../../components/Button/ActionButtons';
import {
  ErrorTag,
  LightTag,
  SuccessTag,
} from '../../../../../../components/Tags';
import GenericDataGrid from '../../../../../components/GenericDataGrid';
import LabelCheckbox from '../../../../../components/LabelCheckbox';
import QuickSearchToolbar from '../../../../../components/QuickSearchToolbar';
import ConfirmationModal from '../../../../../../components/v2/ConfirmationModal';
import { formatRelativeTime } from '../../../../../../utils/date';
import useSessionStore from '../../../../../../hooks/useSessionStore';
import useToast from '../../../../../hooks/useToast';
import useLogger from '../../../../../../hooks/useLogger';
import LoadingOverlay from '../../../../../components/LoadingOverlay';
import { getViewerSectionData } from '../../../../../utils/coachSectionPods';
import {
  PoolItemAction,
  capturePoolItemActioned,
  capturePoolItemReleased,
} from '../../../../../utils/poolTelemetry';
import {
  formatWaitTime,
  getWaitInDays,
  getWaitSortKey,
} from '../../utils/poolQueue';
import { checkIsPriorityNotification } from '../../utils/notificationPriority';

import getColumns from './columns';
import texts from './texts.json';
import {
  Cell,
  CellRow,
  CellCountContainer,
  CellCount,
  CellMetadata,
  CellMetadataIcon,
  ClaimedIcon,
  ActionableTag,
  TableMessage,
  OrderingHint,
  OrderingHintIcon,
} from './styles';
import { checkHasValidClaim, checkIsPriorityClaim } from './utils';

const OrderingHintLabel = () => (
  <OrderingHint>
    <OrderingHintIcon />
    {texts.orderingHint}
  </OrderingHint>
);

const POOL_FILTER_TOOLS = [{ id: 'orderingHint', Component: OrderingHintLabel }];

const NO_VIEWER_PODS = new Map();

const NoSearchResults = () => <TableMessage>{texts.noSearchResults}</TableMessage>;

const PoolQueueClear = () => <TableMessage>{texts.poolQueueClear}</TableMessage>;

const LoadFailed = () => <TableMessage>{texts.loadFailed}</TableMessage>;

const renderActionCell = ({ row: { id: coachId } }) => (
  <PrimaryButton
    onClick={() => window.open(`/c/${coachId}`, '_blank')}
    icon={<ViewIcon />}
    variant="info"
    size="medium"
  >
    {texts.buttons.view}
  </PrimaryButton>
);

const renderLongestWaitCell = ({
  openSectionCount,
  longestWaitingSection,
  oldestOpenAtSeconds,
  isOldestOpenPriority,
}) => (
  <Cell>
    <CellRow $bottomMargin>
      <CellMetadata $isPriority={isOldestOpenPriority}>
        {formatWaitTime(oldestOpenAtSeconds) ?? texts.unknownWait}
      </CellMetadata>
    </CellRow>
    <CellMetadata>
      {format(openSectionCount === 1 ? texts.openSection : texts.openSections, {
        section: texts.sections[longestWaitingSection.section] ?? longestWaitingSection.section,
        count: openSectionCount,
      })}
    </CellMetadata>
  </Cell>
);

const CoachTable = ({
  views,
  isReady,
  hasLoadError,
  isPoolQueue,
  workSummaries,
  podIdByCoachId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pendingTakeover, setPendingTakeover] = useState(null);
  const [showTotalCounts, setShowTotalCounts] = useState(false);

  const {
    userDoc: loggedInUserDoc,
    userId: user,
  } = useContext(LoggedInUserContext);

  const { isCoachAssistant: isLoggedInUserCoachAssistant } = useSessionStore();

  const { showToast } = useToast();

  const { logEvent } = useLogger();

  const isComponentMountedRef = useComponentMounted();

  const viewsRef = useRef(views);
  viewsRef.current = views;

  const summariesRef = useRef(workSummaries);
  summariesRef.current = workSummaries;

  const findView = useCallback((coachId) => viewsRef.current.find(({ id }) => id === coachId), []);

  const runSectionAction = useCallback(async (errorText, action) => {
    try {
      setIsLoading(true);
      await action();
    } catch (error) {
      showToast(format(errorText, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [showToast, isComponentMountedRef]);

  const claimSection = useCallback((cellData, isTakeover) => runSectionAction(
    texts.errors.claimSection,
    async () => {
      const { path, coachId, section } = cellData;
      const view = findView(coachId);

      const claim = view && (isTakeover
        ? await view.reclaim(section, user, loggedInUserDoc.name)
        : await view.claim(section, user, loggedInUserDoc.name));

      if (!claim) {
        throw new Error(texts.errors.sectionUnavailable);
      }

      if (claim.claimedBy !== user) {
        if (isComponentMountedRef.current) {
          setPendingTakeover({ ...cellData, data: claim });
        }
        return;
      }

      if (section === Section.ROUTING) {
        logEvent('ac_notification_resolved', {
          coach_id: coachId,
          ac_user_id: user,
          resolution_timestamp: new Date().toISOString(),
        });
      }

      if (isPoolQueue) {
        const { oldestNotificationAt } = cellData.data ?? {};
        capturePoolItemActioned(user, {
          coachId,
          section,
          itemAgeDays: oldestNotificationAt ? getWaitInDays(oldestNotificationAt.seconds) : null,
          action: isTakeover ? PoolItemAction.TAKEN_OVER : PoolItemAction.CLAIMED,
        });
      }

      window.open(path, '_blank');
    },
  ), [
    runSectionAction,
    findView,
    isComponentMountedRef,
    isPoolQueue,
    user,
    loggedInUserDoc,
    logEvent,
  ]);

  const handleClaimClick = useCallback((cellData) => {
    if (isPoolQueue) {
      claimSection(cellData, false);
      return;
    }

    if (checkHasValidClaim(cellData.data ?? {})) {
      setPendingTakeover(cellData);
      return;
    }

    claimSection(cellData, true);
  }, [isPoolQueue, claimSection]);

  const handleTakeoverConfirm = useCallback(() => {
    setPendingTakeover(null);

    if (pendingTakeover) {
      claimSection(pendingTakeover, true);
    }
  }, [pendingTakeover, claimSection]);

  const handleReleaseClick = useCallback(({ coachId, section }) => runSectionAction(
    texts.errors.releaseSection,
    async () => {
      const isReleased = await findView(coachId)?.unclaim(section, user);

      if (!isReleased) {
        throw new Error(texts.errors.claimNotHeld);
      }

      capturePoolItemReleased(user, { coachId, section });
    },
  ), [runSectionAction, findView, user]);

  const renderClaimButton = useCallback((cellData) => isLoggedInUserCoachAssistant && (
    <PrimaryButton
      onClick={() => handleClaimClick(cellData)}
      variant="warning"
      size="small"
    >
      {texts.buttons.claim}
    </PrimaryButton>
  ), [isLoggedInUserCoachAssistant, handleClaimClick]);

  const renderPoolClaimState = useCallback((cellData) => {
    const { data: { claimedBy, claimedByUsername, claimedAt } = {} } = cellData;

    if (!claimedBy) {
      return renderClaimButton(cellData);
    }

    if (claimedBy === user) {
      return (
        <>
          <SuccessTag>
            <ClaimedIcon />
            {texts.claimedByMe}
          </SuccessTag>
          {isLoggedInUserCoachAssistant && (
            <PillButton
              onClick={() => handleReleaseClick(cellData)}
              variant="info"
              size="small"
            >
              {texts.buttons.release}
            </PillButton>
          )}
        </>
      );
    }

    const claimedTime = claimedAt ? formatRelativeTime(moment.unix(claimedAt.seconds)) : '';
    const StandingClaimTag = (!!claimedAt && checkIsPriorityClaim(claimedAt)) ? ErrorTag : LightTag;
    const standingClaimTag = (
      <StandingClaimTag>
        {format(texts.claimedByOther, { username: claimedByUsername, time: claimedTime })}
      </StandingClaimTag>
    );

    if (!isLoggedInUserCoachAssistant) {
      return standingClaimTag;
    }

    return (
      <Tooltip
        arrow
        placement="top-start"
        title={format(texts.claimedByOtherTooltip, { username: claimedByUsername, time: claimedTime })}
      >
        <ActionableTag onClick={() => setPendingTakeover(cellData)}>
          {standingClaimTag}
        </ActionableTag>
      </Tooltip>
    );
  }, [
    isLoggedInUserCoachAssistant,
    user,
    renderClaimButton,
    handleReleaseClick,
  ]);

  /**
   * The pod the viewer works on each coach, from their own internal assignments.
   *
   * Empty once the viewer unticks the pod scope, which is what puts the coach's whole book back.
   */
  const viewerPodIdByCoachId = showTotalCounts ? NO_VIEWER_PODS : podIdByCoachId;

  /*
   * An AC working a pod is only accountable for that pod, so the toggle is only worth offering to
   * someone who actually holds one.
   *
   * Ticked means pod-scoped, which is how the table already opens for them. The box has to name the
   * scope it is in rather than the alternative: labelled "Show total counts" and left unticked, it
   * was the only thing on screen saying the numbers were narrowed at all, and it said so by naming
   * what it was not doing — a coach row of all `-` then reads as "nothing pending" rather than
   * "nothing in your pod".
   */
  const podFilterTools = useMemo(() => (
    podIdByCoachId.size
      ? [{
        Component: LabelCheckbox,
        id: 'show-pod-only',
        props: {
          description: texts.showPodOnly,
          isChecked: !showTotalCounts,
          onChange: (isPodOnly) => setShowTotalCounts(!isPodOnly),
        },
      }]
      : []
  ), [podIdByCoachId, showTotalCounts]);

  /**
   * The count a section column sorts and filters on.
   *
   * Scoped exactly as renderItemCell scopes what it draws, so the column orders by the numbers on
   * screen rather than by the coach totals sitting behind them.
   */
  const itemValueGetter = useCallback(({ section, sectionData, coachId }) => getViewerSectionData({
    section,
    sectionData,
    podId: viewerPodIdByCoachId.get(coachId),
  })?.unreadCount || 0, [viewerPodIdByCoachId]);

  const renderItemCell = useCallback((cellData) => {
    const { path, section, coachId } = cellData;
    // Narrowed to the viewer's own pod on this coach, where they have one.
    const {
      unreadCount = 0,
      automatedCount = 0,
      oldestNotificationAt,
      claimedBy,
      claimedByUsername,
      claimedAt,
    } = getViewerSectionData({
      section,
      sectionData: cellData.data,
      podId: viewerPodIdByCoachId.get(coachId),
    }) ?? {};

    if (!unreadCount) {
      return texts.emptyCell;
    }

    const isPriorityNotification = checkIsPriorityNotification(oldestNotificationAt?.seconds, section);

    return (
      <Cell>
        <CellRow $bottomMargin>
          <CellCountContainer onClick={() => window.open(path, '_blank')}>
            <CellCount>{unreadCount}</CellCount>
            <ArrowRight />
          </CellCountContainer>
          {isPoolQueue ? renderPoolClaimState(cellData) : renderClaimButton(cellData)}
        </CellRow>
        {!!automatedCount && (
          <CellRow>
            <CellMetadata>{format(texts.automatedCount, { count: automatedCount })}</CellMetadata>
            <Tooltip
              arrow
              placement="top-start"
              title={format(texts.automatedCountTooltip, { count: automatedCount, total: unreadCount })}
            >
              <CellMetadataIcon />
            </Tooltip>
          </CellRow>
        )}
        {!!oldestNotificationAt && (
          <CellMetadata $isPriority={isPriorityNotification}>
            {format(texts.oldestItemTime, { time: formatRelativeTime(moment.unix(oldestNotificationAt.seconds)) })}
          </CellMetadata>
        )}
        {!isPoolQueue && !!claimedBy && !!claimedAt && (
          <CellRow>
            <CellMetadata $isPriority={checkIsPriorityClaim(claimedAt)}>
              {format(texts.claimInfo, { time: formatRelativeTime(moment.unix(claimedAt.seconds)) })}
            </CellMetadata>
            <Tooltip
              arrow
              placement="top-start"
              title={format(texts.claimInfoTooltip, { username: claimedByUsername })}
            >
              <CellMetadataIcon />
            </Tooltip>
          </CellRow>
        )}
      </Cell>
    );
  }, [
    isPoolQueue,
    renderPoolClaimState,
    renderClaimButton,
    viewerPodIdByCoachId,
  ]);

  const longestWaitColumn = useMemo(() => ({
    field: 'longestWait',
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    headerName: texts.columns.longestWait,
    flex: 2,
    renderCell: ({ row: { id } }) => {
      const summary = summariesRef.current.get(id);
      return summary?.longestWaitingSection ? renderLongestWaitCell(summary) : texts.emptyCell;
    },
    valueGetter: ({ row: { id } }) => getWaitSortKey(summariesRef.current.get(id)),
  }), []);

  const columns = useMemo(() => getColumns({
    actionsRenderCell: renderActionCell,
    itemRenderCell: renderItemCell,
    itemValueGetter,
    extraColumns: isPoolQueue ? [longestWaitColumn] : [],
  }), [
    renderItemCell,
    itemValueGetter,
    isPoolQueue,
    longestWaitColumn,
  ]);

  const {
    filteredRows,
    toolbarProps,
  } = useQuickSearch(views, columns);

  const isSearchFilteredEmpty = !!views.length && !filteredRows.length;
  const claimConfirmTexts = isPoolQueue ? texts.takeoverModal : texts.confirmModal;
  const clearOverlay = isPoolQueue ? PoolQueueClear : null;
  const availableOverlay = isSearchFilteredEmpty ? NoSearchResults : clearOverlay;
  const noRowsOverlayComponent = hasLoadError ? LoadFailed : availableOverlay;

  const components = useMemo(() => ({
    Toolbar: QuickSearchToolbar,
    ...(noRowsOverlayComponent && { NoRowsOverlay: noRowsOverlayComponent }),
  }), [noRowsOverlayComponent]);

  return (
    <>
      <GenericDataGrid
        rows={filteredRows}
        columns={columns}
        components={components}
        rowHeight={110}
        loading={!isReady}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            placeholder: texts.toolbarPlaceholder,
            filterTools: isPoolQueue ? POOL_FILTER_TOOLS : podFilterTools,
          },
        }}
      />
      <LoadingOverlay isLoading={isLoading} />
      {!!pendingTakeover && (
        <ConfirmationModal
          open
          title={claimConfirmTexts.title}
          description={format(claimConfirmTexts.content, {
            username: pendingTakeover.data?.claimedByUsername,
            time: pendingTakeover.data?.claimedAt
              ? moment.unix(pendingTakeover.data.claimedAt.seconds).fromNow()
              : '',
          })}
          onConfirm={handleTakeoverConfirm}
          onClose={() => setPendingTakeover(null)}
        />
      )}
    </>
  );
};

CoachTable.propTypes = {
  views: PropTypes.array.isRequired,
  isReady: PropTypes.bool.isRequired,
  hasLoadError: PropTypes.bool,
  isPoolQueue: PropTypes.bool,
  workSummaries: PropTypes.instanceOf(Map),
  podIdByCoachId: PropTypes.instanceOf(Map),
};

CoachTable.defaultProps = {
  hasLoadError: false,
  isPoolQueue: false,
  workSummaries: new Map(),
  podIdByCoachId: NO_VIEWER_PODS,
};

export default compose(
  observer,
)(CoachTable);
